import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, X, Hotel, AlertCircle, ShieldCheck } from "lucide-react";
import api, { formatDate, resolveImageUrl } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";

export default function HotelConfirmPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const initialAction = searchParams.get("action") || "";
    const site = useSiteContent();
    const brand = site?.brand || {};
    const brandName = brand.name || "Msonline Travel";

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [view, setView] = useState(initialAction === "reject" ? "reject" : "summary"); // summary | reject | success-confirm | success-reject
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get(`/hotel-confirm/${token}`)
            .then((r) => setBooking(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Bağlantı geçersiz"))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        // Auto-confirm if URL action=confirm
        if (initialAction === "confirm" && booking && view === "summary" && !submitting) {
            doSubmit("confirm");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking]);

    const doSubmit = async (action) => {
        setSubmitting(true);
        try {
            await api.post(`/hotel-confirm/${token}`, { action, reject_reason: action === "reject" ? reason : "" });
            setView(action === "confirm" ? "success-confirm" : "success-reject");
        } catch (e) {
            setError(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Center><p className="text-slate-500">Yükleniyor...</p></Center>;
    }
    if (error && !booking) {
        return <Center>
            <div className="text-center">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Geçersiz Bağlantı</h1>
                <p className="text-slate-600">{error}</p>
            </div>
        </Center>;
    }

    const b = booking;
    const pnr = b.id.slice(0, 8).toUpperCase();

    if (view === "success-confirm") {
        return <ResultCard
            icon={<ShieldCheck className="h-16 w-16 text-emerald-500" />}
            title="Rezervasyon Konfirme Edildi"
            desc={`PNR ${pnr} numaralı rezervasyon başarıyla onaylandı. ${brandName} ekibine durum iletildi. Teşekkür ederiz.`}
            color="emerald"
        />;
    }
    if (view === "success-reject") {
        return <ResultCard
            icon={<X className="h-16 w-16 text-red-500" />}
            title="Rezervasyon Reddedildi"
            desc={`PNR ${pnr} için belirttiğiniz sebep ${brandName} ekibine iletildi.`}
            color="red"
        />;
    }

    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4">
            <div className="max-w-2xl mx-auto">
                <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-br from-sky-500 to-indigo-600 px-6 py-6 text-white">
                        <div className="flex items-start gap-3">
                            {brand.logo_url && (
                                <div className="bg-white/95 rounded-lg p-1.5 shadow-sm">
                                    <img src={resolveImageUrl(brand.logo_url)} alt={brandName} className="h-10 max-h-10 w-auto object-contain" />
                                </div>
                            )}
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-widest opacity-85">{brandName} · Otel Konfirmasyon Talebi</div>
                                <div className="font-display text-2xl font-bold mt-1 flex items-center gap-2"><Hotel className="h-5 w-5" /> {b.hotel_name}</div>
                                <div className="text-sm opacity-90 mt-1">PNR <span className="font-mono font-black text-lg">{pnr}</span></div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Box label="Giriş" value={formatDate(b.check_in)} accent />
                            <Box label="Çıkış" value={formatDate(b.check_out)} accent />
                            <Box label="Gece / Oda" value={`${b.nights} gece · ${b.rooms} oda`} />
                            <Box label="Misafir" value={`${b.adults}y ${b.children}ç`} />
                            <Box label="Oda Tipi" value={b.room_type_name} />
                            <Box label="Destinasyon" value={b.destination_name} />
                        </div>

                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Misafir Bilgileri</div>
                            <div className="font-semibold text-slate-900">{b.guest.first_name} {b.guest.last_name}</div>
                            <div className="text-sm text-slate-600">{b.guest.email}</div>
                            {b.guest.phone && <div className="text-sm text-slate-600">{b.guest.phone}</div>}
                        </div>

                        {b.special_requests && (
                            <div className="p-4 rounded-xl bg-sky-50 border border-sky-100">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 mb-1">Misafir İsteği</div>
                                <div className="text-sm text-slate-800">{b.special_requests}</div>
                            </div>
                        )}

                        {view === "summary" && (
                            <div className="pt-2 grid grid-cols-2 gap-3">
                                <button
                                    data-testid="hc-confirm"
                                    onClick={() => doSubmit("confirm")}
                                    disabled={submitting}
                                    className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold disabled:opacity-60"
                                >
                                    <Check className="h-5 w-5" /> Konfirme Et
                                </button>
                                <button
                                    data-testid="hc-reject-open"
                                    onClick={() => setView("reject")}
                                    disabled={submitting}
                                    className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold disabled:opacity-60"
                                >
                                    <X className="h-5 w-5" /> Konfirme Etme
                                </button>
                            </div>
                        )}

                        {view === "reject" && (
                            <div className="pt-2 space-y-3">
                                <div className="text-sm font-semibold text-slate-900">Lütfen reddetme sebebini açıklayın:</div>
                                <textarea
                                    data-testid="hc-reject-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={4}
                                    placeholder="Örn: Belirtilen tarihler için müsaitlik yok, oda satıştan kaldırıldı, vb."
                                    className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-sky-400 text-sm"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <button onClick={() => setView("summary")} disabled={submitting} className="py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold disabled:opacity-60">Vazgeç</button>
                                    <button
                                        data-testid="hc-reject-submit"
                                        onClick={() => doSubmit("reject")}
                                        disabled={submitting || !reason.trim()}
                                        className="py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold disabled:opacity-60"
                                    >
                                        {submitting ? "Gönderiliyor..." : "Reddetmeyi Gönder"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {error && <p className="text-sm text-red-600">{error}</p>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Center({ children }) {
    return <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">{children}</div>;
}

function Box({ label, value, accent }) {
    return (
        <div className={`rounded-xl p-3 ${accent ? "bg-sky-50 border border-sky-100" : "bg-slate-50 border border-slate-100"}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            <div className={`text-sm font-semibold ${accent ? "text-sky-900" : "text-slate-900"}`}>{value || "-"}</div>
        </div>
    );
}

function ResultCard({ icon, title, desc, color }) {
    return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
            <div className={`max-w-md w-full rounded-2xl bg-white border-2 ${color === "emerald" ? "border-emerald-200" : "border-red-200"} p-8 text-center shadow-lg`}>
                <div className="flex justify-center mb-4">{icon}</div>
                <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">{title}</h1>
                <p className="text-slate-600">{desc}</p>
            </div>
        </div>
    );
}
